import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { NavigationAppearance } from "@/pages/Settings/NavigationAppearance";

type ThemeMode = "light" | "dark" | "system";
interface LanguageOption {
    code: string;
    label: string;
    flagIcon: string;
}

export const SettingsAppearanceTab: React.FC = () => {
    const { t, i18n } = useTranslation("global");
    const [theme, setTheme] = useState<ThemeMode>(() => {
        if (typeof window !== "undefined") {
            return (
                (localStorage.getItem("vigepra_theme") as ThemeMode) || "light"
            );
        }
        return "light";
    });

    const [zoom, setZoom] = useState<number>(() => {
        if (typeof window !== "undefined") {
            return Number(localStorage.getItem("vigepra_zoom")) || 100;
        }
        return 100;
    });

    const handleZoomChange = (newZoom: number) => {
        const val = Math.max(50, Math.min(200, newZoom));
        setZoom(val);
        localStorage.setItem("vigepra_zoom", String(val));

        // Use CSS variable to scale only text, reset document zoom just in case
        (document.documentElement.style as any).zoom = "";
        // Apply +0.1 offset so that 100% in UI is 1.1 in CSS
        document.documentElement.style.setProperty(
            "--text-scale",
            String(val / 100 + 0.1)
        );
    };

    const handleSelectTheme = (mode: ThemeMode): void => {
        setTheme(mode);
        localStorage.setItem("vigepra_theme", mode);

        if (mode === "dark") {
            document.documentElement.classList.add("dark");
        } else {
            if (mode === "light") {
                document.documentElement.classList.remove("dark");
            } else {
                const prefersDark = window.matchMedia(
                    "(prefers-color-scheme: dark)"
                ).matches;
                document.documentElement.classList.toggle("dark", prefersDark);
            }
        }
    };

    const handleSelectLanguage = (lang: string): void => {
        i18n.changeLanguage(lang);
        localStorage.setItem("i18nextLng", lang);
    };

    const languageOptions: LanguageOption[] = [
        { code: "es", label: "Español", flagIcon: "flag_es" },
        { code: "ca", label: "Valencià", flagIcon: "flag_va" },
        { code: "en", label: "English", flagIcon: "flag_en" },
    ];

    return (
        <div className="animate-fade-in max-w-2xl mx-auto w-full space-y-4">
            <div className="box animate-fade-in">
                <NavigationAppearance embedded />
            </div>

                {/* Theme Mode Card */}
                <div className="box animate-fade-in space-y-4 p-6 sm:p-8">
                        <div className="text-center">
                            <h3 className="box-title">
                                {t("settings.appearance.title")}
                            </h3>
                            <p className="box-subtitle">
                                {t("settings.appearance.subtitle")}
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
                            {/* Light Mode */}
                            <button
                                type="button"
                                onClick={() => handleSelectTheme("light")}
                                className={`w-full sm:w-35 p-4 rounded-xl border flex items-center justify-between transition-all cursor-pointer ${
                                    theme === "light"
                                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                                        : "border-slate-200 hover:border-slate-300 bg-white dark:bg-dark-box dark:border-white/20"
                                }`}
                            >
                                <div className="flex items-center space-x-3">
                                    <Icon
                                        value="sun"
                                        size={22}
                                        className={`shrink-0 ${theme === "light" ? "text-amber-500" : "text-slate-600 dark:text-zinc-400"}`}
                                    />
                                    <span
                                        className={`text-xs font-bold ${theme === "light" ? "text-primary" : "text-slate-800 dark:text-zinc-200"}`}
                                    >
                                        {t("settings.appearance.theme_light")}
                                    </span>
                                </div>
                                {theme === "light" && (
                                    <Icon
                                        value="check"
                                        size={16}
                                        className="text-primary shrink-0 ml-2"
                                    />
                                )}
                            </button>

                            {/* Dark Mode */}
                            <button
                                type="button"
                                onClick={() => handleSelectTheme("dark")}
                                className={`w-full sm:w-35 p-4 rounded-xl border flex items-center justify-between transition-all cursor-pointer ${
                                    theme === "dark"
                                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                                        : "border-slate-200 hover:border-slate-300 bg-white dark:bg-dark-box dark:border-white/20"
                                }`}
                            >
                                <div className="flex items-center space-x-3">
                                    <Icon
                                        value="moon"
                                        size={22}
                                        className={`shrink-0 ${theme === "dark" ? "text-indigo-500" : "text-slate-600 dark:text-zinc-400"}`}
                                    />
                                    <span
                                        className={`text-xs font-bold ${theme === "dark" ? "text-primary" : "text-slate-800 dark:text-zinc-200"}`}
                                    >
                                        {t("settings.appearance.theme_dark")}
                                    </span>
                                </div>
                                {theme === "dark" && (
                                    <Icon
                                        value="check"
                                        size={16}
                                        className="text-primary shrink-0 ml-2"
                                    />
                                )}
                            </button>

                            {/* System Mode */}
                            <button
                                type="button"
                                onClick={() => handleSelectTheme("system")}
                                className={`w-full sm:w-35 p-4 rounded-xl border flex items-center justify-between transition-all cursor-pointer ${
                                    theme === "system"
                                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                                        : "border-slate-200 hover:border-slate-300 bg-white dark:bg-dark-box dark:border-white/20"
                                }`}
                            >
                                <div className="flex items-center space-x-3">
                                    <Icon
                                        value="monitor"
                                        size={22}
                                        className={`shrink-0 ${theme === "system" ? "text-slate-800 dark:text-zinc-200" : "text-slate-600 dark:text-zinc-400"}`}
                                    />
                                    <span
                                        className={`text-xs font-bold ${theme === "system" ? "text-primary" : "text-slate-800 dark:text-zinc-200"}`}
                                    >
                                        {t("settings.appearance.theme_system")}
                                    </span>
                                </div>
                                {theme === "system" && (
                                    <Icon
                                        value="check"
                                        size={16}
                                        className="text-primary shrink-0 ml-2"
                                    />
                                )}
                            </button>
                        </div>
                    </div>
                {/* Language Selector Card */}
                <div className="box animate-fade-in space-y-4 p-6 sm:p-8">
                        <div className="text-center">
                            <h3 className="box-title">
                                {t("settings.appearance.language_title")}
                            </h3>
                            <p className="box-subtitle">
                                {t("settings.appearance.language_subtitle")}
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            {languageOptions.map((lang) => {
                                const isCurrent = (
                                    i18n.language || "es"
                                ).startsWith(lang.code);

                                return (
                                    <button
                                        key={lang.code}
                                        type="button"
                                        onClick={() =>
                                            handleSelectLanguage(lang.code)
                                        }
                                        className={`w-full sm:w-35 p-4 rounded-xl border flex items-center justify-between transition-all cursor-pointer ${
                                            isCurrent
                                                ? "border-primary bg-primary/5 ring-1 ring-primary"
                                                : "border-slate-200 hover:border-slate-300 bg-white dark:bg-dark-box dark:border-white/20"
                                        }`}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <Icon
                                                value={lang.flagIcon}
                                                width={24}
                                                height={16}
                                                className="shrink-0"
                                            />
                                            <span
                                                className={`text-xs font-bold ${isCurrent ? "text-primary" : "text-slate-800 dark:text-zinc-200"}`}
                                            >
                                                {lang.label}
                                            </span>
                                        </div>

                                        {isCurrent && (
                                            <Icon
                                                value="check"
                                                size={16}
                                                className="text-primary shrink-0 ml-2"
                                            />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                {/* Zoom Control Card */}
                <div className="box animate-fade-in space-y-4 p-6 sm:p-8">
                        <div className="text-center">
                            <h3 className="box-title">
                                {t("settings.appearance.zoom_title")}
                            </h3>
                            <p className="box-subtitle">
                                {t("settings.appearance.zoom_subtitle")}
                            </p>
                        </div>

                        <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                            {[80, 90, 100, 110, 125].map((preset) => (
                                <button
                                    key={preset}
                                    type="button"
                                    onClick={() => handleZoomChange(preset)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                        zoom === preset
                                            ? "bg-primary text-white shadow-md"
                                            : "bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-zinc-700"
                                    }`}
                                >
                                    {preset}%
                                </button>
                            ))}
                        </div>

                        <div className="flex flex-row items-center justify-between gap-3 max-w-md mx-auto w-full">
                            <button
                                type="button"
                                onClick={() => handleZoomChange(zoom - 10)}
                                className="p-2 text-slate-500 hover:text-primary transition-colors cursor-pointer shrink-0"
                            >
                                <Icon value="minus" size={20} />
                            </button>

                            <input
                                type="range"
                                min="50"
                                max="200"
                                step="5"
                                value={zoom}
                                onChange={(e) =>
                                    handleZoomChange(Number(e.target.value))
                                }
                                className="flex-1 accent-primary h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer min-w-0"
                            />

                            <button
                                type="button"
                                onClick={() => handleZoomChange(zoom + 10)}
                                className="p-2 text-slate-500 hover:text-primary transition-colors cursor-pointer shrink-0"
                            >
                                <Icon value="plus" size={20} />
                            </button>
                        </div>

                        <div className="flex items-center justify-center gap-4 mt-2">
                            <div className="flex items-center gap-1">
                                <input
                                    type="number"
                                    min="50"
                                    max="200"
                                    value={zoom}
                                    onChange={(e) =>
                                        handleZoomChange(Number(e.target.value))
                                    }
                                    className="w-16 text-center text-sm font-bold bg-slate-50 dark:bg-dark-box border border-slate-200 dark:border-white/10 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary focus:border-primary outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                                />
                                <span className="text-sm font-bold text-slate-500">
                                    %
                                </span>
                            </div>

                            {zoom !== 100 && (
                                <button
                                    type="button"
                                    onClick={() => handleZoomChange(100)}
                                    className="text-xs font-bold text-primary hover:text-primary-900 transition-colors flex items-center gap-1 cursor-pointer"
                                >
                                    <Icon value="refresh" size={14} />
                                    {t("settings.appearance.zoom_reset")}
                                </button>
                            )}
                        </div>
                    </div>
        </div>
    );
};
